import React, { useState } from 'react';
import styled from 'styled-components';
import { Upload } from '@mui/icons-material';
import { DUAL_JOINT_LIST_PAGE_TEXT } from './lang';
import type { NominationResultData } from './types';
import {
  Button,
  Dialog,
  Table,
  type HeadCell,
  Loading,
  Alert,
  type AlertDetails,
} from '../../../components';
import { postRequest } from '../../../utils';

const headerCellsData: Array<HeadCell<NominationResultData>> = [
  {
    id: 'no',
    label: DUAL_JOINT_LIST_PAGE_TEXT['NO.'],
    isSortable: false,
    render: row => row.no,
  },
  {
    id: 'applicationNo',
    label: DUAL_JOINT_LIST_PAGE_TEXT.APPLICATION_NO,
    isSortable: false,
    render: row => row.applicationNo,
  },
  {
    id: 'name',
    label: DUAL_JOINT_LIST_PAGE_TEXT.NAME,
    isSortable: false,
    render: row => row.name,
  },

  {
    id: 'entryYear',
    label: DUAL_JOINT_LIST_PAGE_TEXT.ENTRY_YEAR,
    isSortable: false,
    render: row => row.entryYear,
  },
  {
    id: 'departmentAbbr',
    label: DUAL_JOINT_LIST_PAGE_TEXT.DEPARTMENT,
    isSortable: false,
    render: row => row.departmentAbbr,
  },
  {
    id: 'partnerName',
    label: DUAL_JOINT_LIST_PAGE_TEXT.PARTNER,
    isSortable: false,
    render: row => row.partnerName,
  },
  {
    id: 'chiefSupervisor',
    label: DUAL_JOINT_LIST_PAGE_TEXT.CHIEF_SUPERVISOR,
    isSortable: false,
    render: row => row.chiefSupervisor,
  },
  {
    id: 'partnerChiefSupervisor',
    label: DUAL_JOINT_LIST_PAGE_TEXT.PARTNER_SUPERVISOR,
    isSortable: false,
    render: row => row.partnerChiefSupervisor,
  },
];

export const UploadDialog: React.FC = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [nominatedResult, setNominatedResult] = useState<
    NominationResultData[] | undefined
  >(undefined);

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const handleFileUploadError = (uploadError: any): void => {
    setOpenAlert(true);
    setAlertDetails({
      alertType: 'error',
      alertContent: `${DUAL_JOINT_LIST_PAGE_TEXT.FAILED_TO_UPDATE_NOMINATION_LIST} ${uploadError}`,
    });
  };

  const handleNominationUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.target.files === null || e.target.files.length < 1) return;
    const file = e.target.files[0];

    const formData = new FormData();
    formData.append('files', file);

    setLoading(true);
    try {
      const res = await postRequest({
        url: '/eadmin-operation/gs/partner/nominate',
        data: formData,
      });
      setNominatedResult((res.data as NominationResultData[]) ?? undefined);
    } catch (error: any) {
      handleFileUploadError(error?.response?.data?.message ?? error);
    }
    setLoading(false);
  };

  const handleBatchUploadClose = (): void => {
    setNominatedResult(undefined);
    window.location.reload();
  };

  // Solve case that if upload same file twice, the second time won't trigger input on change function
  const handleFileClear = (e: any): void => {
    if (e.target.value !== null) {
      e.target.value = null;
    }
  };

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading && <Loading />}
      <Button
        style={{ textTransform: 'none', minWidth: '200px' }}
        variant="text"
        component="label"
        startIcon={<Upload />}
      >
        {DUAL_JOINT_LIST_PAGE_TEXT.UPLOAD_NOMINATION_LIST}
        <VisuallyHiddenInput
          id="file-input"
          type="file"
          onChange={handleNominationUpload}
          onClick={handleFileClear}
        />
      </Button>
      <Dialog
        openDialog={Boolean(nominatedResult)}
        dialogTitle={DUAL_JOINT_LIST_PAGE_TEXT.UPDATE_NOMINATION_LIST}
        cancelAction={DUAL_JOINT_LIST_PAGE_TEXT.CLOSE}
        onDialogClose={handleBatchUploadClose}
        width="800px"
      >
        <div className="px-4">
          {nominatedResult && (
            <>
              <span className="italic">
                {DUAL_JOINT_LIST_PAGE_TEXT.FAILED_UPDATE_LIST}
              </span>
              <Table
                headCells={headerCellsData}
                data={nominatedResult}
                handleRequestSort={() => {}}
              />
            </>
          )}
        </div>
      </Dialog>
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  width: 100%;
`;

const VisuallyHiddenInput = styled.input`
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  whitespace: nowrap;
  width: 1;
`;
